/**
 * Push Notifications Hook and Manager
 * Handles Web Push subscription and notifications
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check browser support
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkExistingSubscription();
    }
  }, []);

  // Check for existing subscription
  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      
      if (sub) {
        setSubscription(sub);
        setIsSubscribed(true);
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  // Register service worker
  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[Push] Service Worker registrado:', registration);
      return registration;
    } catch (err) {
      console.error('[Push] Error registrando SW:', err);
      throw err;
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Tu navegador no soporta notificaciones push');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        setError('Permiso de notificaciones denegado');
        setLoading(false);
        return false;
      }

      // Register service worker
      await registerServiceWorker();
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const token = localStorage.getItem('token');
      const keyResponse = await axios.get(`${API}/push/vapid-public-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const vapidPublicKey = keyResponse.data.publicKey;

      // Subscribe to push manager
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))))
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscription(sub);
      setIsSubscribed(true);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('[Push] Error subscribing:', err);
      setError(err.message || 'Error al activar notificaciones');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Tell server to remove subscription
      const token = localStorage.getItem('token');
      await axios.post(`${API}/push/unsubscribe`, {
        endpoint: subscription.endpoint,
        keys: {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('[Push] Error unsubscribing:', err);
      setError(err.message || 'Error al desactivar notificaciones');
      setLoading(false);
      return false;
    }
  }, [subscription]);

  // Send test notification
  const sendTest = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/push/test`, {
        title: '🔔 Test CENTINELA',
        body: 'Las notificaciones push están funcionando correctamente'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (err) {
      console.error('[Push] Error sending test:', err);
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  };
}

export default usePushNotifications;
